/**
 * Monthly Calendar Color Enricher
 *
 * Resolves family member colors for calendar events in monthly view.
 * Uses calendar-to-member mappings to determine the appropriate color.
 */

import { EventDescriptionSectionParser } from '@/lib/utils/EventDescriptionSectionParser';
import type { CalendarEvent } from '@/types/events';
import type { CalendarMemberMapping } from '@/types/calendar-member-mapping';
import type { FamilyMember } from '@/types/family-member';

/**
 * Context required for color resolution
 */
export type ColorResolutionContext = {
  calendarMappings: CalendarMemberMapping[];
  familyMembers: FamilyMember[];
  meMemberId: string;
};

/**
 * Static class for resolving family member colors for events
 */
export class MonthlyCalendarColorEnricher {
  /**
   * Resolve family member color for an event
   *
   * Resolution order:
   * 1. Existing label participant color (extendedProperties.participants with role='label')
   * 2. Calendar-to-member mapping (first mapped member's color)
   * 3. Description labels (for members without calendars)
   * 4. Default to "Me" member color
   */
  static resolveFamilyMemberColor(
    event: CalendarEvent,
    context: ColorResolutionContext
  ): string | undefined {
    // 1. Check for existing label participant color
    const labelColor = this.getLabelParticipantColor(event);
    if (labelColor) {
      return labelColor;
    }

    // 2. Check calendar-to-member mapping
    const mappingColor = this.getColorFromCalendarMapping(event, context);
    if (mappingColor) {
      return mappingColor;
    }

    // 3. Check description labels for members without calendars
    const descriptionColor = this.getColorFromDescriptionLabels(event, context);
    if (descriptionColor) {
      return descriptionColor;
    }

    // 4. Default to "Me" member color
    return this.getMeMemberColor(context);
  }

  /**
   * Get color from event's label participant
   */
  private static getLabelParticipantColor(event: CalendarEvent): string | undefined {
    const participants = event.extendedProperties?.participants;
    if (!participants || participants.length === 0) {
      return undefined;
    }

    const labelParticipant = participants.find(p => p.role === 'label');
    return labelParticipant?.familyMemberColor;
  }

  /**
   * Get color from calendar-to-member mapping
   * Uses the first mapped member's color
   */
  private static getColorFromCalendarMapping(
    event: CalendarEvent,
    context: ColorResolutionContext
  ): string | undefined {
    const mapping = context.calendarMappings.find(
      m => m.calendarId === event.deviceCalendarId
    );

    if (!mapping || mapping.memberIds.length === 0) {
      return undefined;
    }

    // Get the first mapped member's color
    const firstMemberId = mapping.memberIds[0];
    const member = context.familyMembers.find(m => m.id === firstMemberId);

    return member?.color;
  }

  /**
   * Get color from description labels
   */
  private static getColorFromDescriptionLabels(
    event: CalendarEvent,
    context: ColorResolutionContext
  ): string | undefined {
    const descriptionMemberIds = EventDescriptionSectionParser.extractFamilyMemberIds(
      event.description
    );

    if (descriptionMemberIds.length === 0) {
      return undefined;
    }

    // Use first labeled member's color
    for (const memberId of descriptionMemberIds) {
      const member = context.familyMembers.find(m => m.id === memberId);
      if (member?.color) {
        return member.color;
      }
    }

    return undefined;
  }

  /**
   * Get the "Me" member's color as default
   */
  private static getMeMemberColor(context: ColorResolutionContext): string | undefined {
    const meMember = context.familyMembers.find(m => m.id === context.meMemberId);
    return meMember?.color;
  }
}
